import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import { removeBackground } from "@imgly/background-removal";
import NotFound from "../NotFound";

// This page is intentionally served at /teststore while it displays the
// production store's packages. Checkout is created through the existing
// stats API so the Tebex token never reaches a visitor's browser.
const STORE_BASE = "/teststore";
const STORE_API_BASE = "https://stats.applesmp.us";
const DISCORD_URL = "https://www.dsc.gg/applesmpofficial";
const RANK_API = "https://luckperms.applesmp.us/groups";

// ----------- Creator Codes -------------
// Add creator codes here. The object key is the URL code:
// /store/chlimro or /teststore/chlimro
// example code                              chlimro: { name: "Chlimro", discountPercent: 20 },
const CREATOR_CODES: Record<string, { name: string; discountPercent?: number }> = {
  smj: { name: "SMJ", discountPercent: 20 },
  chlimro: { name: "Chlimro", discountPercent: 20 },
};

// ─── Types ────────────────────────────────────────────────────────────────────
type StorePackage = {
  id: string;
  slug: string;
  name: string;
  price: string;
  category: string;
  salePercent?: number;
  period?: string;
  badge?: string;
  packageLabel?: "Best Value" | "Most Popular" | "Starter Pick";
  bonus?: string;
  oldAmount?: string;
  featured?: boolean;
  disabled?: boolean;
  perks?: string[];
  image?: string;
};

type CartItem = { package: StorePackage; quantity: number };
type PolicyModal = "storePrivacy" | "applesmpPrivacy" | "refund" | "terms" | null;
type Tab = string;
type TebexCatalogPackage = { id: string; basePrice: number; totalPrice: number; currency: string; disabled: boolean; disableQuantity: boolean };
type TrialStatus = { available: boolean; status: "available" | "unavailable" | "unknown" | "login_required"; appliedToBasket?: boolean };
type TebexQuote = { ident: string; basePrice: number; tax: number; totalPrice: number; currency: string; checkoutUrl: string | null; trial: TrialStatus | null };

// ─── Category Config ──────────────────────────────────────────────────────────
// Flip `enabled` to false to hide a category's tab, kick users off it if they're
// currently viewing it, and strip its items out of any cart. Flip back to true
// to bring it back — nothing else needs to change.
const CATEGORY_CONFIG: Record<string, { label: string; color: string; enabled: boolean }> = {
  ranks: { label: "Ranks", color: "#ff4d5e", enabled: true },
  keys: { label: "Keys", color: "#38bdf8", enabled: true },
  shards: { label: "Shards", color: "#a855f7", enabled: false },
  // Add a future tab with one line, e.g.
  // cosmetics: { label: "Cosmetics", color: "#f59e0b", enabled: true },
};

// ─── Rank weights ─────────────────────────────────────────────────────────────
const RANK_WEIGHTS: Record<string, number> = {
  default: 0,
  seed: 1,
  "seed-rank": 1,
  root: 2,
  "root-rank": 2,
  tree: 3,
  "tree-rank": 3,
  apple: 4,
  "apple-rank": 4,
  "apple+": 5,
  appleplus: 5,
  "apple-plus": 5,
  apple_plus: 5,
  "apple-plus-rank": 5,
  media: 5,
};
const STAFF_GROUPS = new Set(["mod", "moderator", "admin", "co-owner", "coowner", "owner"]);
const APPLE_PLUS_GROUPS = new Set(["apple+", "appleplus", "trialmod", "apple-plus", "apple_plus", "apple-plus-rank"]);

// ─── Packages (from code.txt with correct IDs) ────────────────────────────────
const PACKAGES: Record<string, StorePackage[]> = {
  ranks: [
    {
      id: "7068783",
      slug: "seed-rank",
      name: "Seed Rank",
      price: "6.99",
      category: "rank",
      packageLabel: "Starter Pick",
      image: "seed-rank.png",
      perks: ["3 homes", "50 ah slots", "/hide command", "1.5x discord xp", "4 ender chest rows", "3 seed keys", "2 shards everywhere"],
    },
    {
      id: "7068803",
      slug: "root-rank",
      name: "Root Rank",
      price: "11.99",
      category: "rank",
      image: "root-rank.png",
      perks: ["4 homes", "120 ah slots", "2x discord xp", "5 ender chest rows", "3 spawner keys", "3 shards everywhere"],
    },
    {
      id: "7068804",
      slug: "tree-rank",
      name: "Tree Rank",
      price: "20.99",
      category: "rank",
      image: "tree-rank.png",
      perks: ["6 homes", "190 ah slots", "/disguise command", "3x discord xp", "6 ender chest rows", "3 plant keys", "4 shards everywhere",],
    },
    {
      id: "7068805",
      slug: "apple-rank",
      name: "Apple Rank",
      price: "34.99",
      category: "rank",
      image: "apple-rank.png",
      packageLabel: "Most Popular",
      featured: true,
      perks: ["8 homes", "250 ah slots", "4x discord xp", "5 apple keys", "6 ender chest rows", "3 shards everywhere"],
    },
    {
      id: "7498818",
      slug: "apple-plus-rank",
      name: "Apple+ Rank",
      price: "6.99",
      period: "/month",
      category: "rank",
      image: "apple-plus-rank.png",
      badge: "Subscription",
      packageLabel: "Best Value",
      featured: true,
      perks: ["10 homes", "300 ah slots", "3 apple, amethyst, and spawner keys", "everything from previous ranks", "15m money", "5k shards", "6 shards everywhere"],
    },
  ],
  keys: [
    {
      id: "7068822",
      slug: "apple-crate-key",
      name: "Apple Crate Key",
      price: "4.99",
      category: "key",
      image: "apple-key.png",
      packageLabel: "Most Popular",
      featured: true,
      perks: [
        "Forest armor with Protection IV and Mending",
        "Forest tools with Efficiency V and Unbreaking III",
        "Snout-trim netherite armor rewards",
        "Spawners, shard boosters, and top-tier loot",
      ],
    },
    {
      id: "7068821",
      slug: "amethyst-crate-key",
      name: "Amethyst Crate Key",
      price: "3.99",
      category: "key",
      image: "amethyst-key.png",
      perks: [
        "Amethyst tools with Efficiency V and Mending",
        "Tree chopper, drill, sell axe, and bucket rewards",
        "Shard booster with 4x production for 24 hours",
        "Strong mid-tier utility and money-making items",
      ],
    },
    {
      id: "7068819",
      slug: "spawner-crate-key",
      name: "Spawner Crate Key",
      price: "2.99",
      category: "key",
      image: "spawner-key.png",
      perks: [
        "Cow, zombie, skeleton, and spider spawners",
        "Piglin, blaze, and iron golem spawners",
        "Spawner drops include mob loot and farming items",
        "Best crate for building farms and passive income",
      ],
    },
    {
      id: "7068818",
      slug: "plant-crate-key",
      name: "Plant Crate Key",
      price: "1.99",
      category: "key",
      image: "plant-key.png",
      perks: [
        "Netherite and diamond armor rewards",
        "Diamond and netherite tools with useful enchants",
        "Weapons, pickaxes, axes, and survival gear",
        "Great early-game upgrade crate",
      ],
    },
    {
      id: "7123185",
      slug: "seed-crate-key",
      name: "Seed Crate Key",
      price: "0.99",
      category: "key",
      image: "seed-key.png",
      packageLabel: "Starter Pick",
      perks: [
        "Diamond armor and basic starter gear",
        "Diamond sword, pickaxe, axe, and tools",
        "Useful low-tier enchantments",
        "Cheap starter crate with solid survival rewards",
      ],
    },
  ],
  shards: [
    {
      id: "7502703",
      slug: "400-shards",
      name: "400 Shards",
      price: "1.99",
      category: "shard",
      image: "400-shards.png",
      perks: ["Adds 400 in-game shards", "Use shards for server upgrades", "Delivered after Tebex checkout"],
    },
    {
      id: "7502704",
      slug: "2500-shards",
      name: "2,500 Shards",
      price: "5.99",
      category: "shard",
      image: "2500-shards.png",
      packageLabel: "Starter Pick",
      perks: ["Adds 2,500 in-game shards", "Good starter shard bundle", "Delivered after Tebex checkout"],
    },
    {
      id: "7502705",
      slug: "5500-shards",
      name: "5,500 Shards",
      price: "9.99",
      category: "shard",
      image: "5500-shards.png",
      perks: ["Adds 5,500 in-game shards", "Delivered after Tebex checkout"],
    },
    {
      id: "7502706",
      slug: "12000-shards",
      name: "12,000 Shards",
      price: "19.99",
      category: "shard",
      image: "12000-shards.png",
      perks: ["Adds 12,000 in-game shards", "Delivered after Tebex checkout"],
    },
    {
      id: "7502707",
      slug: "90000-shards",
      name: "90,000 Shards",
      price: "49.99",
      category: "shard",
      image: "32000-shards.png",
      perks: ["Adds 90,000 in-game shards", "Delivered after Tebex checkout"],
    },
    {
      id: "7502709",
      slug: "500000-shards",
      name: "500,000 Shards",
      featured: true,
      price: "159.99",
      category: "shard",
      image: "70000-shards.png",
      packageLabel: "Best Value",
      perks: ["Adds 500,000 in-game shards", "Best shard value"],
    },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getActiveStoreBase() {
  return STORE_BASE;
}

function getCreatorCodeFromPath() {
  const parts = window.location.pathname.split("/").filter(Boolean);
  const base = parts[0];
  const code = parts[1];
  if (base !== "teststore" || !code || code === "checkout") return "";
  return code.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 40);
}

function hasInvalidCreatorCodeInPath() {
  const code = getCreatorCodeFromPath();
  return Boolean(code && !CREATOR_CODES[code]);
}

function formatCreatorName(code: string) {
  if (CREATOR_CODES[code]?.name) return CREATOR_CODES[code].name;
  return code
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || code;
}

function loadTebexScript() {
  if ((window as any).Tebex?.checkout) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const existing = document.getElementById("tebex-js");
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Tebex.js failed to load.")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = "tebex-js";
    script.src = "https://js.tebex.io/v/1.js";
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Tebex.js failed to load."));
    document.head.appendChild(script);
  });
}

async function launchTebexCheckout(ident: string, checkoutUrl: string) {
  try {
    await loadTebexScript();
    const tebex = (window as any).Tebex;
    tebex.checkout.init({
      ident,
      theme: "dark",
      colors: [{ name: "primary", color: "#ff4d5e" }],
    });
    tebex.checkout.launch();
  } catch {
    window.location.href = checkoutUrl;
  }
}

async function safeJson(res: Response) {
  const text = await res.text();
  try { return text ? JSON.parse(text) : {}; } catch { return {}; }
}

async function verifyPlayer(username: string, platform: "java" | "bedrock") {
  const cleanName = username.trim().replace(/^\./, "");
  const lookupName = platform === "bedrock" ? `.${cleanName}` : cleanName;
  const ts = Date.now();

  const lpRes = await fetch(`${RANK_API}/${encodeURIComponent(lookupName)}?_t=${ts}`, { cache: "no-store" });
  if (!lpRes.ok) throw new Error("Player not found on the server. Please join first.");

  let lpGroups: string[] = [];
  const lpData = await safeJson(lpRes);
  if (!lpData || lpData.error || Object.keys(lpData).length === 0)
    throw new Error("Player not found on the server. Please join first.");
  if (Array.isArray(lpData)) lpGroups = lpData.map((g: any) => String(g).trim().toLowerCase());
  else if (Array.isArray(lpData.groups)) lpGroups = lpData.groups.map((g: any) => String(g).trim().toLowerCase());
  else if (typeof lpData.group === "string" && lpData.group) lpGroups = [lpData.group.trim().toLowerCase()];

  return { luckperms: lpData, luckpermsGroups: lpGroups.length > 0 ? lpGroups : null };
}

function parsePlayerGroups(payload: any): string[] {
  if (!payload) return [];
  if (payload.luckpermsGroups && Array.isArray(payload.luckpermsGroups)) return payload.luckpermsGroups;
  const d = payload.stats || {};
  if (Array.isArray(d.groups)) return d.groups.map((g: any) => String(g).trim().toLowerCase()).filter(Boolean);
  if (typeof d.group === "string" && d.group) return [d.group.trim().toLowerCase()];
  return [];
}

function buildStoreRequest(username: string, platform: "java" | "bedrock", cart: CartItem[], storeBase: string, creatorCode: string) {
  const cleanCreatorCode = creatorCode.trim().toLowerCase();
  return {
    username,
    platform,
    items: cart.map(item => ({ id: item.package.id, quantity: item.quantity })),
    creatorCode: cleanCreatorCode,
    origin: window.location.origin,
    returnPath: cleanCreatorCode ? `${storeBase}/${cleanCreatorCode}` : storeBase,
  };
}

async function storeApi<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${STORE_API_BASE}${path}`, { cache: "no-store", ...options });
  const body = await safeJson(response);
  if (!response.ok) throw new Error(String(body?.error || body?.message || "Store request failed."));
  return body as T;
}

function formatTebexMoney(amount: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(amount || 0));
}

// ─── Main Component ───────────────────────────────────────────────────────────
const bgCache = new Map<string, string>();

function BgRemovedImage({ src, alt, style }: { src: string; alt: string; style: CSSProperties }) {
  const [processedSrc, setProcessedSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!src) return;
    if (bgCache.has(src)) {
      setProcessedSrc(bgCache.get(src)!);
      setLoading(false);
      return;
    }
    setLoading(true);
    removeBackground(src)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        bgCache.set(src, url);
        setProcessedSrc(url);
      })
      .catch(() => setProcessedSrc(src))
      .finally(() => setLoading(false));
  }, [src]);

  if (loading) {
    return (
      <div style={{ ...style, background: '#171A21', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,.06)', borderTopColor: '#ff4d5e', borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
      </div>
    );
  }

  return <img src={processedSrc || src} alt={alt} style={style} />;
}

export default function StorePage(): ReactElement {
  const storeBase = getActiveStoreBase();
  const pathCreatorCode = getCreatorCodeFromPath();
  const hasInvalidCreatorCode = hasInvalidCreatorCodeInPath();
  // Auth state
  const [tebexUsername, setTebexUsername] = useState("");
  const [platform, setPlatform] = useState<"java" | "bedrock">("java");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [inputPlatform, setInputPlatform] = useState<"java" | "bedrock">("java");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [playerPayload, setPlayerPayload] = useState<any>(null);
  const [isGuestBrowsing, setIsGuestBrowsing] = useState(false);
  const creatorCode = pathCreatorCode && CREATOR_CODES[pathCreatorCode] ? pathCreatorCode : "";

  // Store state
  const [currentTab, setCurrentTab] = useState<Tab>("ranks");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cartError, setCartError] = useState("");
  const [tebexCatalog, setTebexCatalog] = useState<Record<string, TebexCatalogPackage>>({});
  const [trialStatus, setTrialStatus] = useState<TrialStatus>({ available: false, status: "login_required" });
  const [basketQuote, setBasketQuote] = useState<TebexQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // UI state
  const [showLoadingScreen, setShowLoadingScreen] = useState(true);
  const [loadingLeaving, setLoadingLeaving] = useState(false);
  const [activePolicy, setActivePolicy] = useState<PolicyModal>(null);
  const [isSuccessPage, setIsSuccessPage] = useState(false);

  // Loading screen
  useEffect(() => {
    const f = window.setTimeout(() => setLoadingLeaving(true), 850);
    const h = window.setTimeout(() => setShowLoadingScreen(false), 1220);
    return () => { window.clearTimeout(f); window.clearTimeout(h); };
  }, []);

  // Persist session
  useEffect(() => {
    localStorage.removeItem("applesmp_creator_code");
    const savedUser = localStorage.getItem("applesmp_user");
    const savedPlat = localStorage.getItem("applesmp_platform") as "java" | "bedrock" | null;
    if (savedUser && savedPlat) {
      setTebexUsername(savedUser);
      setPlatform(savedPlat);
      setIsAuthenticated(true);
      verifyPlayer(savedUser, savedPlat)
        .then((res) => setPlayerPayload(res))
        .catch(() => {});
    }
    if (window.location.pathname.includes("/checkout/success")) setIsSuccessPage(true);
  }, []);

  const playerGroups = useMemo(() => parsePlayerGroups(playerPayload), [playerPayload]);

  const hasStaffRank = useMemo(() =>
    playerGroups.some((g) => STAFF_GROUPS.has(g)), [playerGroups]);

  const hasApplePlusRank = useMemo(() =>
    playerGroups.some((g) => APPLE_PLUS_GROUPS.has(g)), [playerGroups]);

  const playerMaxRankWeight = useMemo(() => {
    if (!playerGroups.length) return 0;
    let max = 0;
    for (const g of playerGroups) {
      const w = RANK_WEIGHTS[g];
      if (w !== undefined && w > max) max = w;
    }
    return max;
  }, [playerGroups]);

  useEffect(() => {
    if (!isAuthenticated || !tebexUsername) {
      setTebexCatalog({});
      setTrialStatus({ available: false, status: "login_required" });
      return;
    }
    let cancelled = false;
    storeApi<{ packages: TebexCatalogPackage[]; trial: TrialStatus }>(
      `/store/catalog?username=${encodeURIComponent(tebexUsername)}&platform=${encodeURIComponent(platform)}`
    )
      .then(data => {
        if (cancelled) return;
        setTebexCatalog(Object.fromEntries(data.packages.map(pkg => [pkg.id, pkg])));
      })
      .catch(error => !cancelled && setCartError(error.message || "Could not load Tebex prices."));
    return () => { cancelled = true; };
  }, [isAuthenticated, platform, tebexUsername]);

  // Tebex only tells us whether its native Apple+ trial applies after a basket
  // exists. Make one disposable preview basket after login so the card badge is
  // correct before the player touches their actual cart.
  useEffect(() => {
    if (!isAuthenticated || !tebexUsername) return;
    const applePlus = PACKAGES.ranks.find(pkg => pkg.slug === "apple-plus-rank");
    if (!applePlus) return;
    const cacheKey = `applesmp_apple_plus_trial_preview:${platform}:${tebexUsername.toLowerCase()}`;
    let cached: { status?: "available" | "unavailable"; expiresAt?: number } | null = null;
    try { cached = JSON.parse(sessionStorage.getItem(cacheKey) || "null"); } catch { /* expired/old cache */ }
    if ((cached?.status === "available" || cached?.status === "unavailable") && Number(cached.expiresAt) > Date.now()) {
      setTrialStatus({ available: cached.status === "available", status: cached.status });
      return;
    }
    let cancelled = false;
    storeApi<TebexQuote>("/store/quote", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildStoreRequest(tebexUsername, platform, [{ package: applePlus, quantity: 1 }], storeBase, creatorCode)),
    }).then(data => {
      if (cancelled) return;
      const available = data.basePrice === 0 && data.totalPrice === 0;
      sessionStorage.setItem(cacheKey, JSON.stringify({ status: available ? "available" : "unavailable", expiresAt: Date.now() + 5 * 60 * 1000 }));
      setTrialStatus({ available, status: available ? "available" : "unavailable", appliedToBasket: available });
    }).catch(() => {
      // Keep the normal Best Value badge if Tebex cannot be reached right now.
    });
    return () => { cancelled = true; };
  }, [creatorCode, isAuthenticated, platform, storeBase, tebexUsername]);

  useEffect(() => {
    if (!isAuthenticated || !tebexUsername || cart.length === 0) {
      setBasketQuote(null);
      setQuoteLoading(false);
      return;
    }
    let cancelled = false;
    // Never let an older basket quote be used after the player changes their cart.
    setBasketQuote(null);
    setQuoteLoading(true);
    const timer = window.setTimeout(() => {
      setCartError("");
      storeApi<TebexQuote>("/store/quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildStoreRequest(tebexUsername, platform, cart, storeBase, creatorCode)),
      }).then(data => {
        if (!cancelled) {
          setBasketQuote(data);
          const isApplePlusBasket = cart.some(item => item.package.slug === "apple-plus-rank");
          const trialApplied = isApplePlusBasket && data.basePrice === 0 && data.totalPrice === 0;
          if (trialApplied) {
            // The live Tebex basket takes priority over any older API hint.
            setTrialStatus({ available: true, status: "available", appliedToBasket: true });
          } else if (data.trial) {
            setTrialStatus(data.trial);
          }
        }
      }).catch(error => !cancelled && setCartError(error.message || "Could not update the Tebex basket."))
        .finally(() => !cancelled && setQuoteLoading(false));
    }, 350);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [cart, creatorCode, isAuthenticated, platform, storeBase, tebexUsername]);

  const priceForPackage = useCallback((pkg: StorePackage) => tebexCatalog[pkg.id], [tebexCatalog]);

  const heroTitle = creatorCode ? `Support ${formatCreatorName(creatorCode)}` : "Support AppleSMP";

  const getEligibility = useCallback((pkg: StorePackage): { eligible: boolean; reason: string } => {
    if (pkg.disabled) return { eligible: false, reason: "Unavailable" };
    if (tebexCatalog[pkg.id]?.disabled) return { eligible: false, reason: "Unavailable" };
    if (pkg.category !== "rank") return { eligible: true, reason: "" };
    if (hasApplePlusRank) return { eligible: false, reason: "Owned" };
    if (pkg.slug === "apple-plus-rank") return { eligible: true, reason: "" };
    const w = RANK_WEIGHTS[pkg.slug] || 0;
    if (w > 0 && w <= playerMaxRankWeight) return { eligible: false, reason: "Owned" };
    return { eligible: true, reason: "" };
  }, [hasApplePlusRank, playerMaxRankWeight, tebexCatalog]);

  useEffect(() => {
    setCart((prev) => prev.filter((item) =>
      Object.entries(PACKAGES).some(([tab, packages]) => CATEGORY_CONFIG[tab]?.enabled && packages.some(pkg => pkg.id === item.package.id))
      && getEligibility(item.package).eligible
    ));
  }, [getEligibility]);

  // Bounce off a disabled tab onto the first enabled one
  useEffect(() => {
    if (!CATEGORY_CONFIG[currentTab]?.enabled) {
      const firstEnabled = Object.keys(CATEGORY_CONFIG).find((tab) => CATEGORY_CONFIG[tab].enabled);
      if (firstEnabled) setCurrentTab(firstEnabled);
    }
  }, [currentTab]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!inputValue.trim()) return;
    setLoginLoading(true);
    setLoginError("");
    try {
      const data = await verifyPlayer(inputValue.trim(), inputPlatform);
      localStorage.setItem("applesmp_user", inputValue.trim());
      localStorage.setItem("applesmp_platform", inputPlatform);
      setTebexUsername(inputValue.trim());
      setPlatform(inputPlatform);
      setPlayerPayload(data);
      setIsAuthenticated(true);
      setIsGuestBrowsing(false);
    } catch (err: any) {
      setLoginError(err.message || "An error occurred during verification.");
    } finally {
      setLoginLoading(false);
    }
  }

  function handleGuestBrowse() {
    setLoginError("");
    setCart([]);
    setIsCartOpen(false);
    setCartError("");
    setIsGuestBrowsing(true);
  }

  function handleLogout() {
    localStorage.removeItem("applesmp_user");
    localStorage.removeItem("applesmp_platform");
    setTebexUsername("");
    setIsAuthenticated(false);
    setIsGuestBrowsing(false);
    setCart([]);
    setIsCartOpen(false);
    setPlayerPayload(null);
    setIsSuccessPage(false);
    window.history.pushState({}, "", creatorCode ? `${storeBase}/${creatorCode}` : storeBase);
  }

  function addToCart(pkg: StorePackage) {
    if (!isAuthenticated) return;
    const elig = getEligibility(pkg);
    if (!elig.eligible) return;
    setCart((prev) => {
      const existing = prev.find((i) => i.package.id === pkg.id);
      if (existing) {
        if (pkg.category === "rank") return prev;
        return prev.map((i) => i.package.id === pkg.id ? { ...i, quantity: Math.min(50, i.quantity + 1) } : i);
      }
      return [...prev, { package: pkg, quantity: 1 }];
    });
    setIsCartOpen(true);
  }

  function removeFromCart(id: string) {
    setCart((prev) => prev.filter((i) => i.package.id !== id));
  }

  function updateQty(id: string, delta: number) {
    setCart((prev) => prev
      .map((i) => {
        if (i.package.id !== id) return i;
        if (i.package.category === "rank") return { ...i, quantity: 1 };
        return { ...i, quantity: Math.max(1, Math.min(50, i.quantity + delta)) };
      }));
  }

  async function proceedToCheckout() {
    setCartError("");
    if (!isAuthenticated || !tebexUsername) {
      setCartError("Please enter your Minecraft username before checkout.");
      return;
    }
    if (cart.length === 0) return;
    const blockedItem = cart.find((item) => !getEligibility(item.package).eligible);
    if (blockedItem) {
      setCart((prev) => prev.filter((item) => getEligibility(item.package).eligible));
      setCartError(`${blockedItem.package.name} is not available for your current rank.`);
      return;
    }
    setCheckoutLoading(true);
    try {
      const data = await storeApi<TebexQuote>("/store/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildStoreRequest(tebexUsername, platform, cart, storeBase, creatorCode)),
      });
      if (!data.checkoutUrl) throw new Error("Tebex did not return a checkout URL.");
      await launchTebexCheckout(data.ident, data.checkoutUrl);
    } catch (err: any) {
      setCartError(err.message || "Checkout failed. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  }

  // ─── Loading Screen ──────────────────────────────────────────────────────────
  if (hasInvalidCreatorCode) {
    return <NotFound />;
  }

  if (showLoadingScreen) {
    return (
      <div style={{ ...S.loadingPage, opacity: loadingLeaving ? 0 : 1 }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={S.loadingAppleWrap}>
          <img src="/apple.png" alt="" style={S.loadingApple} />
          <div style={S.spinner} />
        </div>
      </div>
    );
  }
  // ─── Success Page ────────────────────────────────────────────────────────────
  if (isSuccessPage) {
    return (
      <div style={S.pageWrap}>
        <Navbar tebexUsername={tebexUsername} platform={platform} isAuthenticated={isAuthenticated} isGuestBrowsing={false} onLogout={handleLogout} onGuestExit={() => setIsGuestBrowsing(false)} />
        <div style={S.successOuter}>
          <div style={S.successCard}>
            <div style={S.successIcon}>✓</div>
            <h1 style={S.successTitle}>Purchase Complete!</h1>
            <p style={S.successSub}>Thank you for supporting AppleSMP. Your rewards are being delivered to your account.</p>
            <div style={S.successDiscordBox}>
              <h3 style={S.successDiscordTitle}>Need your Discord role?</h3>
              <p style={S.successDiscordText}>Open a billing ticket with your Tebex order ID and Minecraft username.</p>
              <a href={DISCORD_URL} target="_blank" rel="noreferrer" style={S.discordBtn}>Open Discord Ticket</a>
            </div>
            <button style={S.successBackBtn} onClick={() => { setIsSuccessPage(false); window.history.pushState({}, "", creatorCode ? `${storeBase}/${creatorCode}` : storeBase); }}>
              Back to Store
            </button>
          </div>
        </div>
        <Footer onPolicy={setActivePolicy} />
        <PolicyModal active={activePolicy} onClose={() => setActivePolicy(null)} />
      </div>
    );
  }

  // ─── Login Page ──────────────────────────────────────────────────────────────
  if (!isAuthenticated && !isGuestBrowsing) {
    return (
      <div style={S.loginPage}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={S.loginHero}>
          <h1 style={S.loginHeroTitle}>{heroTitle}</h1>
          <p style={S.loginHeroSub}>Enhance your gameplay experience with custom tiers, cosmetics, and premium currency. Most proceeds directly support server hardware and continuous development cycles.</p>
        </div>
        <div style={S.loginCard}>
          <h2 style={S.loginCardTitle}>Enter Storefront</h2>
          <p style={S.loginCardDesc}>Please enter your exact Minecraft username. Bedrock edition accounts do not need to add the "." prefix.</p>
          <form onSubmit={handleLogin} style={S.loginForm}>
            <div style={S.toggleRow}>
              <button type="button" onClick={() => setInputPlatform("java")}
                style={{ ...S.toggleBtn, ...(inputPlatform === "java" ? S.toggleActive : {}) }}>
                Java Edition
              </button>
              <button type="button" onClick={() => setInputPlatform("bedrock")}
                style={{ ...S.toggleBtn, ...(inputPlatform === "bedrock" ? S.toggleActive : {}) }}>
                Bedrock Edition
              </button>
            </div>
            <input
              type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)}
              placeholder="Enter Minecraft Username" style={S.loginInput} disabled={loginLoading}
            />
            {loginError && <div style={S.loginError}>{loginError}</div>}
            <button type="submit" style={S.loginSubmitBtn} disabled={loginLoading || !inputValue.trim()}>
              {loginLoading ? "Synchronizing Session..." : "Continue to Packages"}
            </button>
            <button type="button" style={S.loginGuestBtn} onClick={handleGuestBrowse} disabled={loginLoading}>
              Browse as Guest
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Main Store ───────────────────────────────────────────────────────────────
  const currentPackages = PACKAGES[currentTab] || [];
  const currentCategory = CATEGORY_CONFIG[currentTab];

  return (
    <div style={S.pageWrap}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <Navbar tebexUsername={tebexUsername} platform={platform} isAuthenticated={isAuthenticated} isGuestBrowsing={isGuestBrowsing} onLogout={handleLogout} onGuestExit={() => setIsGuestBrowsing(false)} />

      {/* Hero */}
      <div style={S.storeHero}>
        <h1 style={S.storeHeroTitle}>{heroTitle}</h1>
        <p style={S.storeHeroSub}>Enhance your gameplay experience with custom tiers, cosmetics, and premium currency. Most proceeds directly support server hardware and business costs.</p>
      </div>

      {/* Tabs */}
      <div style={S.tabsBar}>
        {Object.entries(CATEGORY_CONFIG)
          .filter(([, category]) => category.enabled)
          .map(([tab, category]) => (
            <button key={tab} onClick={() => setCurrentTab(tab)}
              style={{ ...S.tabBtn, ...(currentTab === tab ? { ...S.tabBtnActive, color: category.color } : {}) }}>
              {category.label}
            </button>
          ))}
      </div>

      {/* Cards Grid */}
      <div style={S.storeBody}>
        <div style={S.cardsGrid}>
          {currentPackages.map((pkg) => {
            const elig = getEligibility(pkg);
            const isOwned = !elig.eligible && elig.reason === "Owned";
            const catalogPackage = priceForPackage(pkg);
            const displayedPrice = catalogPackage ? formatTebexMoney(catalogPackage.basePrice, catalogPackage.currency) : "Loading...";
            const canAddToCart = isAuthenticated && elig.eligible && Boolean(catalogPackage);
            const catColor = currentCategory?.color || "#ff4d5e";
            const isApplePlus = pkg.slug === "apple-plus-rank";
            const topBadge = isApplePlus && trialStatus.available
              ? "1 Day Free Trial"
              : (pkg.packageLabel || pkg.badge || "");
            return (
              <div key={pkg.id} style={{ ...S.card, ...(isOwned ? S.cardOwned : {}) }}>
                {pkg.image && (
                  <div style={S.cardBgClip}>
                    <img src={`/${pkg.image}`} alt="" aria-hidden="true" style={S.cardBgImage} />
                  </div>
                )}
            {/* Badges */}
            {topBadge && <div style={S.badgeTop}>{topBadge.toUpperCase()}</div>}
            {pkg.salePercent ? <div style={S.saleBadge}>-{pkg.salePercent}% SALE</div> : null}
          
            {/* Bonus / oldAmount chips */}
            {pkg.bonus && (
              <div style={S.chipRow}>
                <span style={S.chipGreen}>{pkg.bonus}</span>
              </div>
            )}
          
            {/* Name */}
          <h3 style={{ ...S.cardName, color: catColor, ...(isOwned ? S.cardNameOwned : {}) }}>{pkg.name}</h3>
          
          {/* Price */}
          <div style={S.priceRow}>
            <span style={{ ...S.priceMain, ...(isOwned ? { color: "#555" } : {}) }}>
              {displayedPrice}{pkg.period ? <span style={S.pricePeriod}>{pkg.period}</span> : null}
            </span>
          </div>

            {/* Perks */}
            <div style={S.perkList}>
              {(pkg.perks || []).map((perk) => (
                <div key={perk} style={S.perkRow}>
                  <span style={{ ...S.perkCheck, color: catColor, ...(isOwned ? { color: "#444" } : {}) }}>✓</span>
                  <span style={{ ...S.perkText, ...(isOwned ? { color: "#444" } : {}) }}>{perk}</span>
                </div>
              ))}
            </div>
          
            {/* CTA */}
            <button
              onClick={() => addToCart(pkg)}
              disabled={!canAddToCart}
              style={{
                ...S.addBtn,
                ...(isOwned ? S.addBtnOwned : {}),
                ...(!isOwned && canAddToCart ? { cursor: "pointer", background: catColor, border: `1px solid ${catColor}` } : {}),
              }}
            >
              {!isAuthenticated ? "Login to Buy" : isOwned ? "Owned" : elig.eligible ? "Add to Basket" : elig.reason}
            </button>
          </div>
                      );
                    })}
                  </div>
      </div>

      {cart.length > 0 && !isCartOpen && (
        <button style={S.floatingCart} onClick={() => setIsCartOpen(true)}>
          Basket ({cart.reduce((sum, item) => sum + item.quantity, 0)})
        </button>
      )}
      {/* Cart Drawer */}
      {isCartOpen && (
        <div style={S.drawerOverlay}>
          <div style={S.drawer}>
            <div style={S.drawerHeader}>
              <h2 style={S.drawerTitle}>Shopping Basket</h2>
              <button style={S.drawerClose} onClick={() => setIsCartOpen(false)}>✕</button>
            </div>

            {cartError && <div style={S.cartError}>{cartError}</div>}

            {cart.length === 0 ? (
              <div style={S.cartEmpty}>Your basket is empty.</div>
            ) : (
              <div style={S.drawerBody}>
                <div style={S.cartItemsList}>
                  {cart.map((item) => {
                    const catalogPackage = priceForPackage(item.package);
                    const unitPrice = catalogPackage
                      ? formatTebexMoney(catalogPackage.basePrice, catalogPackage.currency)
                      : "Loading...";
                    const hasApplePlusTrial = item.package.slug === "apple-plus-rank" && trialStatus.available;
                    return (
                      <div key={item.package.id} style={S.cartItem}>
                        <div style={S.cartItemMeta}>
                          <span style={S.cartItemName}>{item.package.name}</span>
                          <span style={S.cartPriceLine}>
                            <span style={S.cartItemPrice}>
                              {unitPrice}{item.package.period ? ` ${item.package.period}` : ""}
                            </span>
                          </span>
                          {hasApplePlusTrial && <span style={S.cartTrialBadge}>1 DAY FREE TRIAL</span>}
                        </div>
                        <div style={S.cartItemRight}>
                          {item.package.category !== "rank" && (
                            <div style={S.stepper}>
                              <button style={S.stepBtn} onClick={() => updateQty(item.package.id, -1)}>-</button>
                              <span style={S.stepVal}>{item.quantity}</span>
                              <button style={S.stepBtn} onClick={() => updateQty(item.package.id, 1)}>+</button>
                            </div>
                          )}
                          <button style={S.removeBtn} onClick={() => removeFromCart(item.package.id)}>Remove</button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={S.drawerFooter}>
                  <div style={S.totalLine}>
                    <span style={S.totalLabel}>Total Balance Due:</span>
                    <span style={S.totalPriceStack}>
                      <span style={S.totalAmt}>
                        {quoteLoading ? "Updating…" : basketQuote ? formatTebexMoney(basketQuote.totalPrice, basketQuote.currency) : "—"}
                      </span>
                    </span>
                  </div>
                  {basketQuote && basketQuote.basePrice !== basketQuote.totalPrice && (
                    <p style={S.creatorDiscountLine}>
                      Tebex applied all eligible discounts and tax.
                    </p>
                  )}
                  {basketQuote?.trial?.appliedToBasket && <p style={S.creatorDiscountLine}>Apple+ 1-day free trial applied for this username.</p>}
                  <p style={S.checkoutDisclaimer}>
                    By clicking checkout, you agree to our server regulations, store privacy policies, terms of service and our final refund terms.
                  </p>
                  <button style={S.checkoutBtn} onClick={proceedToCheckout} disabled={checkoutLoading || quoteLoading || !basketQuote}>
                    {checkoutLoading ? "Redirecting to Tebex Gateway..." : quoteLoading ? "Updating Tebex Basket..." : "Proceed to Checkout"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer onPolicy={setActivePolicy} />
      <PolicyModal active={activePolicy} onClose={() => setActivePolicy(null)} />
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Navbar({ tebexUsername, platform, isAuthenticated, isGuestBrowsing, onLogout, onGuestExit }: {
  tebexUsername: string; platform: "java" | "bedrock"; isAuthenticated: boolean; isGuestBrowsing: boolean; onLogout: () => void; onGuestExit: () => void;
}) {
  return (
    <nav style={S.navbar}>
      <div style={S.navLeft}>
        <img src="/apple.png" alt="AppleSMP" style={S.navLogo} />
        <span style={S.navBrand}>AppleSMP Store</span>
      </div>
      {isAuthenticated ? (
        <div style={S.navRight}>
          <img
            src={platform === "bedrock"
              ? "https://minihead.webui.anexia.cloud/avatar/steve/64.png"
              : `https://mc-heads.net/avatar/${tebexUsername}/64`}
            alt={tebexUsername}
            style={S.navAvatar}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
          <div style={S.navMeta}>
            <span style={S.navUsername}>{tebexUsername}</span>
            <span style={S.navPlatform}>{platform === "java" ? "Java Edition" : "Bedrock"}</span>
          </div>
          <button style={S.logoutBtn} onClick={onLogout}>Logout</button>
        </div>
      ) : isGuestBrowsing ? (
        <div style={S.navRight}>
          <div style={S.navMeta}>
            <span style={S.navUsername}>Guest</span>
            <span style={S.navPlatform}>Browse Only</span>
          </div>
          <button style={S.logoutBtn} onClick={onGuestExit}>Sign In</button>
        </div>
      ) : null}
    </nav>
  );
}

function Footer({ onPolicy }: { onPolicy: (m: PolicyModal) => void }) {
  return (
    <footer style={S.footer}>
      <div style={S.footerLinks}>
        <button style={S.footerLink} onClick={() => onPolicy("terms")}>Terms of Service</button>
        <button style={S.footerLink} onClick={() => onPolicy("refund")}>Refund Policy</button>
        <button style={S.footerLink} onClick={() => onPolicy("storePrivacy")}>Store Privacy Policy</button>
        <button style={S.footerLink} onClick={() => onPolicy("applesmpPrivacy")}>AppleSMP Privacy Policy</button>
      </div>
      <p style={S.footerCopy}>© {new Date().getFullYear()} AppleSMP™. All rights reserved. We are not affiliated with Mojang Studios, Microsoft, or any official corporate entities.</p>
    </footer>
  );
}

function PolicyModal({ active, onClose }: { active: PolicyModal; onClose: () => void }) {
  if (!active) return null;
  const titles: Record<string, string> = {
    terms: "Terms of Service", refund: "Refund Policy",
    storePrivacy: "Store Privacy Policy", applesmpPrivacy: "AppleSMP Privacy Policy",
  };
  const texts: Record<string, string[]> = {
    terms: [
      "By accessing or purchasing from our platform, you agree that all items sold are intangible digital goods for entertainment inside AppleSMP. Purchases do not grant ownership equity, intellectual property rights, administrative access, or any right to transfer, resell, or duplicate rewards.",
      "Packages may be changed, replaced, paused, or removed if required for gameplay balance, server updates, security, or rule enforcement. Rule violations, abuse of store systems, exploit use, or attempts to bypass restrictions may result in penalties up to permanent suspension without refund.",
    ],
    refund: [
      "Because purchases are fulfilled as digital in-game rewards through Tebex, all sales are generally final. AppleSMP rarely allows refunds, and only under very limited circumstances such as duplicate billing, a clear checkout mistake reported quickly, or a delivery issue we cannot reasonably fix.",
      "If a package does not arrive, the wrong username was entered, or you believe there was a billing problem, open a Discord billing ticket with your Tebex order ID, Minecraft username, and a short description before contacting your bank.",
      "Starting a chargeback, bank dispute, or payment reversal without working with support first may result in an IP ban from the AppleSMP store and the Minecraft server. This chargeback ban does not apply to the Discord server.",
    ],
    storePrivacy: [
      "AppleSMP checkout is powered by Tebex. During checkout, Tebex may process information such as your Minecraft username, selected packages, transaction details, billing contact details, payment metadata, device or browser information, fraud-prevention signals, support messages, and records needed for legal, tax, security, and payment-processing purposes.",
      "Tebex may share data with payment processors, fraud-prevention providers, hosting and support vendors, legal or compliance partners, and other service providers needed to run the checkout platform. AppleSMP does not receive or store full payment card numbers or bank credentials.",
    ],
    applesmpPrivacy: [
      "AppleSMP uses your entered Minecraft username and selected platform to verify that the player exists on the server, display your account in the store, create a Tebex basket, deliver purchased rewards, and help staff resolve billing tickets.",
      "We may temporarily use your permission group data to decide which rank packages are available to buy. We do not store full payment details on AppleSMP infrastructure. Support tickets may include your Minecraft username, order ID, screenshots, and messages you provide so staff can investigate delivery or billing issues.",
    ],
  };
  const links: Partial<Record<NonNullable<PolicyModal>, { label: string; href: string }>> = {
    storePrivacy: { label: "Read Tebex's full Privacy Policy", href: "https://www.tebex.io/terms-creator-agreement/privacy-policy" },
  };
  const link = links[active];
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <h2 style={S.modalTitle}>{titles[active]}</h2>
          <button style={S.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={S.modalText}>
          {texts[active].map((text) => <p key={text} style={S.modalParagraph}>{text}</p>)}
          {link && <a href={link.href} target="_blank" rel="noreferrer" style={S.modalLink}>{link.label}</a>}
        </div>
        {active === "refund" && (
          <div style={S.discordBox}>
            <h4 style={S.discordBoxTitle}>Need Transaction Assistance?</h4>
            <p style={S.discordBoxText}>Open a billing ticket before filing a dispute so staff can review the order.</p>
            <a href={DISCORD_URL} target="_blank" rel="noreferrer" style={S.discordBtn}>Join Support Discord</a>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S: Record<string, CSSProperties> = {
  // Loading
  loadingPage: { position: "fixed", inset: 0, zIndex: 99999, background: "#050608", display: "flex", alignItems: "center", justifyContent: "center", transition: "opacity .35s ease", pointerEvents: "none", overflow: "hidden" },
  loadingAppleWrap: { position: "relative", width: "min(520px, 82vw)", height: "min(520px, 82vw)", display: "flex", alignItems: "center", justifyContent: "center" },
  loadingApple: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", imageRendering: "auto", filter: "grayscale(1) opacity(0.16) contrast(1.08) drop-shadow(0 18px 42px rgba(0,0,0,0.45))", transform: "translateZ(0)" },
  spinner: { width: 34, height: 34, border: "3px solid rgba(255,255,255,.08)", borderTopColor: "#ffffff", borderRightColor: "rgba(255,255,255,.35)", borderRadius: "50%", animation: "spin .7s linear infinite", position: "relative", zIndex: 1, boxShadow: "0 0 22px rgba(255,255,255,0.08)" },

  // Page wrap
  pageWrap: { minHeight: "100vh", background: "#0B0C0E", color: "#EFF1F5", fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", display: "flex", flexDirection: "column" },

  // Navbar
  navbar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 36px", background: "#111317", borderBottom: "1px solid #1C1F26", position: "sticky", top: 0, zIndex: 100 },
  navLeft: { display: "flex", alignItems: "center", gap: 12 },
  navLogo: { width: 36, height: 36, objectFit: "contain" },
  navBrand: { fontSize: 17, fontWeight: 800, color: "#fff", letterSpacing: "-0.2px" },
  navRight: { display: "flex", alignItems: "center", gap: 10, background: "#171A21", padding: "6px 6px 6px 12px", borderRadius: 30, border: "1px solid #232936" },
  navAvatar: { width: 28, height: 28, borderRadius: 6, objectFit: "cover", background: "#0B0C0E" },
  navMeta: { display: "flex", flexDirection: "column" },
  navUsername: { fontSize: 13, fontWeight: 700, color: "#fff", lineHeight: 1.2 },
  navPlatform: { fontSize: 10, color: "#646D7F", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" },
  logoutBtn: { background: "#2B3242", border: "none", color: "#EFF1F5", padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer" },

  // Login page
  loginPage: { minHeight: "100vh", background: "#0B0C0E", color: "#EFF1F5", fontFamily: "system-ui,-apple-system,sans-serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", boxSizing: "border-box" },
  loginHero: { textAlign: "center", padding: "60px 20px 24px", maxWidth: 700 },
  loginHeroTitle: { fontSize: 36, fontWeight: 900, color: "#fff", margin: "0 0 14px", letterSpacing: "-0.5px" },
  loginHeroSub: { fontSize: 15, color: "#646D7F", lineHeight: 1.6, margin: 0 },
  loginCard: { width: "100%", maxWidth: 440, background: "#111317", border: "1px solid #1C1F26", borderRadius: 16, padding: "36px 32px", boxSizing: "border-box", boxShadow: "0 20px 40px rgba(5,5,6,0.4)" },
  loginCardTitle: { fontSize: 22, fontWeight: 800, color: "#fff", margin: "0 0 8px", textAlign: "center" },
  loginCardDesc: { fontSize: 13, color: "#646D7F", lineHeight: 1.5, margin: "0 0 28px", textAlign: "center" },
  loginForm: { display: "flex", flexDirection: "column", gap: 16 },
  toggleRow: { display: "flex", background: "#0B0C0E", padding: 4, borderRadius: 10, border: "1px solid #1C1F26" },
  toggleBtn: { flex: 1, padding: "10px", background: "none", border: "none", color: "#646D7F", fontSize: 13, fontWeight: 700, borderRadius: 7, cursor: "pointer" },
  toggleActive: { background: "#171A21", color: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.2)" },
  loginInput: { width: "100%", boxSizing: "border-box", background: "#0B0C0E", border: "1px solid #1C1F26", borderRadius: 10, padding: "14px 16px", color: "#fff", fontSize: 14, fontWeight: 600, outline: "none" },
  loginError: { fontSize: 13, color: "#FF4D6A", background: "rgba(255,77,106,0.06)", padding: "12px 16px", borderRadius: 10, border: "1px solid rgba(255,77,106,0.15)", lineHeight: 1.4 },
  loginSubmitBtn: { width: "100%", background: "#EFF1F5", color: "#0B0C0E", border: "none", borderRadius: 10, padding: "14px", fontSize: 14, fontWeight: 800, cursor: "pointer" },
  loginGuestBtn: { width: "100%", background: "#171A21", color: "#EFF1F5", border: "1px solid #232936", borderRadius: 10, padding: "14px", fontSize: 14, fontWeight: 800, cursor: "pointer" },

  // Store hero
  storeHero: { textAlign: "center", padding: "50px 20px 28px", maxWidth: 760, margin: "0 auto" },
  storeHeroTitle: { fontSize: 34, fontWeight: 900, color: "#fff", margin: "0 0 12px", letterSpacing: "-0.5px" },
  storeHeroSub: { fontSize: 15, color: "#646D7F", lineHeight: 1.6, margin: 0 },

  // Tabs
  tabsBar: { display: "flex", justifyContent: "center", gap: 4, padding: "0 20px 20px", borderBottom: "1px solid #1C1F26", maxWidth: 1200, margin: "0 auto", width: "100%", boxSizing: "border-box" },
  tabBtn: { padding: "10px 22px", background: "none", border: "none", color: "#646D7F", fontSize: 15, fontWeight: 700, cursor: "pointer", borderRadius: 8 },
  tabBtnActive: { color: "#fff", background: "#111317" },

  // Store body + cards
  storeBody: { flex: 1, maxWidth: 1200, width: "100%", margin: "0 auto", padding: "32px 20px 100px", boxSizing: "border-box" },
  cardsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24 },

  // Card
  card: { background: "#111317", border: "1px solid #1C1F26", borderRadius: 16, padding: 28, display: "flex", flexDirection: "column", position: "relative", boxSizing: "border-box" },
  cardOwned: { opacity: 0.55, borderColor: "rgba(255,77,94,0.2)" },
  badgeTop: { position: "absolute", top: -12, right: 24, background: "#2B3242", color: "#EFF1F5", fontSize: 10, fontWeight: 800, padding: "4px 12px", borderRadius: 20, border: "1px solid #3A4356", textTransform: "uppercase", letterSpacing: "0.5px" },
  saleBadge: { position: "absolute", top: -12, left: 24, background: "#ff4d5e", color: "#fff", fontSize: 10, fontWeight: 900, padding: "4px 12px", borderRadius: 20 },
  chipRow: { display: "flex", gap: 6, marginBottom: 10 },
  chipGray: { background: "rgba(255,255,255,0.07)", color: "#8e8e93", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4 },
  chipGreen: { background: "rgba(0,230,118,0.08)", color: "#00e676", fontSize: 10, fontWeight: 900, padding: "2px 8px", borderRadius: 4, border: "1px solid rgba(0,230,118,0.15)" },
  cardName: { fontSize: 24, fontWeight: 800, color: "#fff", margin: "0 0 10px" },
  cardNameOwned: { textDecoration: "line-through", color: "rgba(255,255,255,0.3)" },
  oldPrice: { fontSize: 14, color: "#646D7F", textDecoration: "line-through", fontWeight: 600 },
  priceMain: { fontSize: 18, fontWeight: 900, color: "#fff" },
  pricePeriod: { fontSize: 13, color: "#646D7F", fontWeight: 600 },
  perkList: { display: "flex", flexDirection: "column", gap: 10, flex: 1, marginBottom: 24, position: "relative", zIndex: 1 },
  perkRow: { display: "flex", alignItems: "flex-start", gap: 8 },
  priceRow: { display: "flex", alignItems: "baseline", gap: 6, marginBottom: 18, position: "relative", zIndex: 1 },
  perkCheck: { color: "#646D7F", fontSize: 12, fontWeight: 700, marginTop: 1 },
  perkText: { fontSize: 13, color: "#8491A8", lineHeight: 1.4 },
  addBtn: { width: "100%", background: "#171A21", border: "1px solid #232936", color: "#fff", borderRadius: 10, padding: "12px", fontSize: 13, fontWeight: 700, cursor: "not-allowed", position: "relative", zIndex: 1 },
  addBtnOwned: { background: "#0B0C0E", border: "1px solid #16191F", color: "#3F4654" },
  cardHeaderRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 },
  cardHeaderLeft: { flex: 1, minWidth: 0 },
  cardImageWrap: { flexShrink: 0, width: 72, height: 72 },
  cardImage: { width: "100%", height: "100%", objectFit: "contain", display: "block" },
  cardBgClip: { position: "absolute", inset: 0, borderRadius: 16, overflow: "hidden", pointerEvents: "none", zIndex: 0 },
  cardBgImage: { position: "absolute", right: -10, bottom: -10, width: "55%", height: "75%", objectFit: "contain", opacity: 0.19, userSelect: "none" },

  // Floating cart
  floatingCart: { position: "fixed", bottom: 28, right: 28, background: "#EFF1F5", color: "#0B0C0E", border: "none", borderRadius: 50, padding: "16px 28px", fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", zIndex: 9999 },

  // Cart drawer
  drawerOverlay: { position: "fixed", inset: 0, background: "rgba(5,5,6,0.7)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "flex-end", zIndex: 10000 },
  drawer: { width: "100%", maxWidth: 460, background: "#111317", borderLeft: "1px solid #1C1F26", height: "100%", display: "flex", flexDirection: "column", boxShadow: "-10px 0 40px rgba(0,0,0,0.3)" },
  drawerHeader: { padding: "24px 28px", borderBottom: "1px solid #1C1F26", display: "flex", justifyContent: "space-between", alignItems: "center" },
  drawerTitle: { fontSize: 18, fontWeight: 800, color: "#fff", margin: 0 },
  drawerClose: { background: "none", border: "none", color: "#646D7F", fontSize: 16, cursor: "pointer" },
  cartError: { margin: "16px 28px 0", background: "rgba(255,77,106,0.06)", border: "1px solid rgba(255,77,106,0.15)", borderRadius: 10, padding: "12px 16px", color: "#FF4D6A", fontSize: 13 },
  cartEmpty: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#646D7F", fontSize: 14 },
  drawerBody: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  cartItemsList: { flex: 1, overflowY: "auto", padding: "24px 28px", display: "flex", flexDirection: "column", gap: 14 },
  cartItem: { background: "#0B0C0E", border: "1px solid #1C1F26", borderRadius: 12, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  cartItemMeta: { display: "flex", flexDirection: "column", gap: 4 },
  cartItemName: { fontSize: 14, fontWeight: 700, color: "#fff" },
  cartItemPrice: { fontSize: 13, color: "#8491A8", fontWeight: 600 },
  cartTrialBadge: { alignSelf: "flex-start", marginTop: 3, padding: "3px 6px", borderRadius: 4, background: "rgba(255,77,94,0.16)", border: "1px solid rgba(255,77,94,0.42)", color: "#ff7180", fontSize: 9, fontWeight: 900, letterSpacing: 0.4 },
  cartPriceLine: { display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" },
  cartOldPrice: { fontSize: 13, color: "#646D7F", fontWeight: 600, textDecoration: "line-through" },
  cartDiscountPrice: { fontSize: 13, color: "#fff", fontWeight: 900 },
  cartItemRight: { display: "flex", alignItems: "center", gap: 14 },
  stepper: { display: "flex", alignItems: "center", background: "#171A21", borderRadius: 6, border: "1px solid #232936", padding: 2 },
  stepBtn: { width: 26, height: 26, background: "none", border: "none", color: "#EFF1F5", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  stepVal: { fontSize: 13, fontWeight: 700, color: "#fff", minWidth: 20, textAlign: "center" },
  removeBtn: { background: "none", border: "none", color: "#ff4d5e", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  drawerFooter: { padding: "20px 28px 28px", borderTop: "1px solid #1C1F26" },
  totalLine: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 },
  totalLabel: { fontSize: 14, fontWeight: 700, color: "#646D7F" },
  totalPriceStack: { display: "flex", alignItems: "baseline", gap: 8 },
  totalOldAmt: { fontSize: 16, color: "#646D7F", fontWeight: 700, textDecoration: "line-through" },
  totalAmt: { fontSize: 26, fontWeight: 900, color: "#fff" },
  creatorDiscountLine: { fontSize: 12, color: "#00e676", fontWeight: 700, margin: "0 0 12px" },
  trialNotice: { margin: "0 0 14px", padding: "8px 10px", borderRadius: 8, background: "rgba(0,230,118,0.08)", border: "1px solid rgba(0,230,118,0.24)", color: "#00e676", fontSize: 11, fontWeight: 700, lineHeight: 1.35 },
  checkoutDisclaimer: { fontSize: 11, color: "#646D7F", lineHeight: 1.4, margin: "0 0 18px" },
  checkoutBtn: { width: "100%", background: "#EFF1F5", color: "#0B0C0E", border: "none", borderRadius: 10, padding: "14px", fontSize: 14, fontWeight: 800, cursor: "pointer" },

  // Footer
  footer: { background: "#111317", borderTop: "1px solid #1C1F26", padding: "28px 40px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, marginTop: "auto" },
  footerLinks: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px 24px" },
  footerLink: { background: "none", border: "none", color: "#646D7F", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  footerCopy: { fontSize: 11, color: "#3F4654", textAlign: "center", lineHeight: 1.5, margin: 0, maxWidth: 680 },

  // Success page
  successOuter: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" },
  successCard: { width: "100%", maxWidth: 500, background: "#111317", border: "1px solid #1C1F26", borderRadius: 20, padding: "48px 36px", textAlign: "center", boxSizing: "border-box" },
  successIcon: { width: 64, height: 64, borderRadius: "50%", background: "rgba(0,230,118,0.1)", border: "2px solid rgba(0,230,118,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, color: "#00e676", margin: "0 auto 20px" },
  successTitle: { fontSize: 28, fontWeight: 900, color: "#fff", margin: "0 0 10px" },
  successSub: { fontSize: 14, color: "#8491A8", lineHeight: 1.6, margin: "0 0 28px" },
  successDiscordBox: { background: "rgba(88,101,242,0.06)", border: "1px solid rgba(88,101,242,0.2)", borderRadius: 12, padding: "20px", marginBottom: 28 },
  successDiscordTitle: { fontSize: 16, fontWeight: 800, color: "#fff", margin: "0 0 6px" },
  successDiscordText: { fontSize: 13, color: "#8491A8", lineHeight: 1.5, margin: "0 0 14px" },
  successBackBtn: { width: "100%", background: "#1C1F26", border: "1px solid #282B30", color: "#fff", borderRadius: 10, padding: "13px", fontSize: 14, fontWeight: 700, cursor: "pointer" },

  // Policy modal
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(11,12,14,0.5)", backdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999, padding: 20 },
  modalCard: { width: "100%", maxWidth: 520, background: "rgba(19,21,24,0.92)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: 0, boxShadow: "0 20px 50px rgba(0,0,0,0.4)", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)" },
  modalTitle: { fontSize: 16, fontWeight: 800, color: "#fff", margin: 0 },
  modalClose: { background: "none", border: "none", color: "#646D7F", fontSize: 16, cursor: "pointer" },
  modalText: { fontSize: 13, color: "#a1a8b5", lineHeight: 1.6, margin: 0, padding: "20px 24px" },
  modalParagraph: { margin: "0 0 12px" },
  modalLink: { color: "#8FB4FF", fontSize: 13, fontWeight: 700, textDecoration: "none" },
  discordBox: { background: "rgba(88,101,242,0.05)", border: "1px solid rgba(88,101,242,0.15)", borderRadius: 8, padding: "20px 24px", margin: "0 24px 24px" },
  discordBoxTitle: { fontSize: 14, fontWeight: 800, color: "#fff", margin: "0 0 6px" },
  discordBoxText: { fontSize: 13, color: "#a1a8b5", lineHeight: 1.5, margin: "0 0 14px" },
  discordBtn: { display: "inline-block", background: "#5865F2", color: "#fff", textDecoration: "none", padding: "10px 20px", borderRadius: 6, fontWeight: 700, fontSize: 13 },
};
